//
// Copyright (c) 2025 Comcast
//

import { useRef, forwardRef } from 'react'
import { Form } from 'react-final-form'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import TableForm from 'components/Shared/TableForm/Index'
import TableFormFieldsBuilder from 'components/Shared/TableFormFieldsBuilder/Index'
import TableFormButtons from 'components/Shared/TableForm/TableFormButtons'
import TableFormFooter from 'components/Shared/TableForm/TableFormFooter'
import RequiredLegend from 'components/Shared/TableForm/RequiredLegend'

import { confirm } from 'components/Shared/ConfirmDialog/Index'

const StyledRequiredLegend = styled(RequiredLegend)`
  position: absolute;
  margin: 16px 0 0 16px;
`

const MAX_WIDTH = 944

const PageForm = forwardRef(({
  fieldParams, initialValues, onSubmit, onCancel, actionButtonTitle,
  confirmOnClose, confirmOnSubmit, additionalButton, mutators,
  disableActionButton, keepDirtyOnReinitialize, flatFieldNames, className,
}, ref) => {
  const formConfigRef = useRef({
    handleSubmit: null,
    pristine: null,
    form: null,
  })
  const { t } = useTranslation()

  const onSubmit_ = (event) => {
    const { handleSubmit } = formConfigRef.current

    if (confirmOnSubmit) {
      confirm({
        title: t('common.confirmChanges'),
        message: t('common.areYouSure'),
        confirmLabel: t('common.confirm'),
        cancelLabel: t('common.cancel'),
      }).then(
        () => {
          handleSubmit(event)
        },
        () => {}
      )
    } else {
      handleSubmit(event)
    }
  }

  const onCancel_ = () => {
    const { pristine } = formConfigRef.current
    if (!pristine && confirmOnClose) {
      confirm({ title: t('common.cancel'), message: t('common.areYouSure') }).then(
        () => {
          onCancel()
        },
        () => {}
      )
    } else {
      onCancel()
    }
  }

  if (!fieldParams) {
    return null
  }

  // 'subscription' is used for performance in input fields and needs to be in sync with used form parameters
  // it looks like adding any boolean to 'subscription' does not degrade performance but absence of it does
  // see https://github.com/final-form/react-final-form-arrays/issues/22#issuecomment-822488262

  // We wanted to use separate initialValue param for each field, but have to use shared
  // initialValues due to https://github.com/final-form/react-final-form/issues/880

  // We need keepDirtyOnReinitialize to correctly handle AsyncSelect fields: without it they will be overriden by init values
  // https://github.com/final-form/react-final-form/issues/246
  // We also set it to keep entered form values after error
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      mutators={mutators}
      keepDirtyOnReinitialize={keepDirtyOnReinitialize || false}
      subscription={{ invalid: true, pristine: true }}
    >
      {({ handleSubmit, pristine, form, invalid }) => {
        formConfigRef.current = { handleSubmit, pristine, form }
        if (ref) {
          ref.current = form
        }
        const actionButtonDisabled = disableActionButton || pristine || invalid

        // Send form mutators to additionalButton so it can use them (for example to change form's value)
        const additionalButton_ = mutators ? additionalButton(form.mutators) : additionalButton

        const PageFormFooter = () => {
          return (
            <TableFormFooter>
              <StyledRequiredLegend fieldParams={fieldParams} />
              <TableFormButtons
                primaryButton={{
                  label: actionButtonTitle || t('common.submit'),
                  onClick: (e) => onSubmit_(e),
                  disabled: actionButtonDisabled,
                }}
                secondaryButton={{
                  label: t('common.cancel'),
                  onClick: onCancel_,
                }}
                additionalButton={additionalButton_}
              />
            </TableFormFooter>
          )
        }

        return (
          <TableForm width={MAX_WIDTH + 'px'} noBody className={className}>
            <TableFormFieldsBuilder
              fieldParams={fieldParams}
              flatFieldNames={flatFieldNames}
              form={form}
            />
            <PageFormFooter />
          </TableForm>
        )
      }}
    </Form>
  )
})

PageForm.displayName = 'PageForm'

PageForm.propTypes = {
  fieldParams: PropTypes.array.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionButtonTitle: PropTypes.string,
  confirmOnClose: PropTypes.bool,
  confirmOnSubmit: PropTypes.bool,
  additionalButton: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  mutators: PropTypes.object,
  disableActionButton: PropTypes.bool,
  keepDirtyOnReinitialize: PropTypes.bool,
  flatFieldNames: PropTypes.bool,
}

export default PageForm
